import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {
    Box,
    Flex,
    Stack,
    Text,
    useMultiStyleConfig,
    useDisclosure,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    useCustomPreferenceWithFallback,
    useCustomPreferenceWithLocale,
} from '../../hooks/use-custom-preference'
import {CareShieldIcon} from '../icons'
import Checkbox from '../checkbox'
import Dropdown from '../dropdown'
import WarrantyCareModal from '../modals/warranty-care-modal'
import {buildWarrantyOption, getWarrantyDisplayDescription} from '../../utils/product-utils'

const ProductCare = ({recommendations, onSelectionChange}) => {
    const styles = useMultiStyleConfig('ProductCare')
    const intl = useIntl()
    const {currency: activeCurrency} = useCurrency()
    const [selectedItems, setSelectedItems] = useState({})
    const [expandedItems, setExpandedItems] = useState({})
    const [selectedVariants, setSelectedVariants] = useState({})
    const {isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose} = useDisclosure()
    const [modalWarrantyItem, setModalWarrantyItem] = useState(null)
    const warrantyTermsAndConditionsLinkLocale = useCustomPreferenceWithLocale(
        'warrantyTermsAndConditionsLink',
    )
    const warrantyTermsAndConditionsLinkFallback = useCustomPreferenceWithFallback(
        'warrantyTermsAndConditionsLink',
    )
    const warrantyTermsAndConditionsLink =
        warrantyTermsAndConditionsLinkLocale || warrantyTermsAndConditionsLinkFallback

    const directWarrantyItems = useMemo(() => {
        return (recommendations || [])
            .filter((rec) => Array.isArray(rec?.variants) && rec.variants.length > 0)
            .map((rec) => {
                const firstVariant = rec?.variants?.[0]
                const extensionPeriod = Number(firstVariant?.warrantyExtensionPeriod || 0)

                return {
                    id: rec?.warrantyGroupId || firstVariant?.productId || rec?.recommendedItemId,
                    name:
                        rec?.warrantyGroupName ||
                        firstVariant?.productName ||
                        rec?.recommendedItemId,
                    description:
                        rec?.warrantyDescription ||
                        (extensionPeriod > 0
                            ? `Extend coverage by ${extensionPeriod} months.`
                            : ''),
                    learnMoreHref: rec?.warrantyTermsAndConditionsLink,
                    variants: rec?.variants || [],
                    price: Number(firstVariant?.price || 0),
                    listPrice: Number(firstVariant?.priceMax || firstVariant?.listPrice || 0),
                    rawRec: rec,
                }
            })
            .filter((item) => item?.id)
    }, [recommendations])

    const recommendedProductIds = useMemo(() => {
        const ids = (recommendations || [])
            .filter((rec) => !Array.isArray(rec?.variants) || rec.variants.length === 0)
            .map((rec) => rec?.recommendedItemId)
            .filter(Boolean)

        return [...new Set(ids)]
    }, [recommendations])

    const {data: productsData, isLoading} = useProducts(
        {
            parameters: {
                ids: recommendedProductIds.join(','),
                expand: ['availability'],
            },
            headers: {
                'x-debug': 'product-care',
            },
        },
        {
            enabled: recommendedProductIds.length > 0,
        },
    )

    const products = useMemo(() => {
        return (productsData?.data || []).filter((item) => item?.inventory?.orderable !== false)
    }, [productsData?.data])

    const careItems = useMemo(() => {
        const fetchedItems = products.map((item) => ({
            id: item?.id,
            name: item?.c_variantIdentifier || item?.name || item?.productName,
            warrantyGroupName: item?.warrantyGroupName || item?.name || item?.productName,
            warrantyExtensionPeriod: Number(
                item?.warrantyExtensionPeriod || item?.serviceDuration || 0,
            ),
            description: item?.shortDescription || item?.c_subscriptionShortDescription || '',
            learnMoreHref: item?.link,
            variants: [],
            price: Number(item?.price || 0),
            listPrice: Number(item?.priceMax || item?.listPrice || item?.price || 0),
            rawRec: null,
        }))

        const itemsById = new Map()

        ;[...directWarrantyItems, ...fetchedItems].forEach((item) => {
            if (item?.id && !itemsById.has(item.id)) {
                itemsById.set(item.id, item)
            }
        })

        return Array.from(itemsById.values())
    }, [directWarrantyItems, products])

    const careItemsKey = useMemo(() => {
        return careItems
            .map((item) => item?.id)
            .filter(Boolean)
            .sort()
            .join(',')
    }, [careItems])

    const isOpaqueVariantId = (value) => {
        if (typeof value !== 'string') {
            return false
        }

        return /^[a-f0-9]{24,32}$/i.test(value)
    }

    const isValidWarrantyProductId = (value) => {
        if (typeof value !== 'string') {
            return false
        }

        const normalized = value.trim()
        if (!normalized) {
            return false
        }

        return !isOpaqueVariantId(normalized)
    }

    const getFirstValidVariantIndex = (variants = []) => {
        return variants.findIndex((variant) => isValidWarrantyProductId(variant?.productId))
    }

    const getResolvedVariant = (variants = [], preferredIndex) => {
        const selectedVariant = variants[preferredIndex]

        if (selectedVariant && isValidWarrantyProductId(selectedVariant?.productId)) {
            return selectedVariant
        }

        const firstValidIndex = getFirstValidVariantIndex(variants)
        if (firstValidIndex < 0) {
            return null
        }

        return variants[firstValidIndex]
    }

    function getWarrantyOptionLabel(variant) {
        const extensionMonths = Number(
            variant?.warrantyExtensionPeriod || variant?.serviceDuration || 0,
        )
        if (extensionMonths <= 0) {
            return ''
        }

        return buildWarrantyOption(extensionMonths, intl.formatMessage)
    }

    const selectedCareItems = useMemo(() => {
        return careItems
            .filter((item) => Boolean(selectedItems[item.id]))
            .map((item) => {
                const hasVariants = Array.isArray(item?.variants) && item.variants.length > 0

                if (!hasVariants) {
                    return item
                }

                const selectedVariantIndex = selectedVariants[item.id]
                const selectedVariant = getResolvedVariant(item.variants, selectedVariantIndex)
                if (!selectedVariant) {
                    return null
                }

                const selectedVariantProductId = selectedVariant?.productId || item?.productId

                if (!selectedVariantProductId) {
                    return null
                }

                const selectedVariantPrice = Number(selectedVariant?.price || 0)
                const selectedVariantListPrice = Number(
                    selectedVariant?.priceMax || selectedVariant?.listPrice || selectedVariantPrice,
                )

                return {
                    ...item,
                    productId: selectedVariantProductId,
                    price: selectedVariantPrice,
                    listPrice: selectedVariantListPrice,
                    warrantyGroupName: item?.warrantyGroupName || item?.name,
                    warrantyExtensionPeriod: Number(
                        selectedVariant?.warrantyExtensionPeriod ||
                            selectedVariant?.serviceDuration ||
                            0,
                    ),
                    serviceDuration: Number(
                        selectedVariant?.serviceDuration ||
                            selectedVariant?.warrantyExtensionPeriod ||
                            0,
                    ),
                    name: getWarrantyOptionLabel(selectedVariant) || item.name,
                }
            })
            .filter(Boolean)
    }, [careItems, selectedItems, selectedVariants])

    const selectedCareItemsKey = useMemo(() => {
        return selectedCareItems
            .map((item) => `${item?.id}:${item?.price || 0}:${item?.listPrice || 0}`)
            .filter(Boolean)
            .sort()
            .join(',')
    }, [selectedCareItems])

    useEffect(() => {
        // Keep selection map aligned with currently available care items.
        setSelectedItems((prev) => {
            const next = {}
            careItems.forEach((item) => {
                if (prev[item.id]) {
                    next[item.id] = true
                }
            })
            return next
        })
    }, [careItemsKey])

    useEffect(() => {
        if (onSelectionChange) {
            onSelectionChange(selectedCareItems)
        }
    }, [onSelectionChange, selectedCareItemsKey])

    if ((recommendedProductIds.length > 0 && isLoading) || !careItems.length) {
        return null
    }

    const getDisplayDescription = (item) => {
        return getWarrantyDisplayDescription(item?.description)
    }

    const getWarrantyBadgeLabel = (item) => {
        const hasVariants = Array.isArray(item?.variants) && item.variants.length > 0
        if (!hasVariants) return ''

        const selectedVariantIndex = selectedVariants[item.id] ?? 0
        const selectedVariant = item.variants[selectedVariantIndex] || item.variants[0]

        return (
            selectedVariant?.badgeCommerce1 ||
            selectedVariant?.badgeCommerce2 ||
            selectedVariant?.badgeCommerce3 ||
            ''
        )
    }

    const handleCardDeselect = (itemId) => {
        // Deselect and collapse
        setSelectedItems((prev) => ({
            ...prev,
            [itemId]: false,
        }))
        setExpandedItems((prev) => ({
            ...prev,
            [itemId]: false,
        }))
        setSelectedVariants((prev) => ({
            ...prev,
            [itemId]: undefined,
        }))
    }

    const handleCheckboxToggle = (itemId, checked) => {
        if (checked) {
            const selectedItem = careItems.find((item) => item.id === itemId)
            const hasVariants =
                Array.isArray(selectedItem?.variants) && selectedItem.variants.length > 0

            setSelectedVariants((prev) => {
                const next = {}

                if (prev[itemId] !== undefined) {
                    next[itemId] = prev[itemId]
                } else if (hasVariants) {
                    const firstValidIndex = getFirstValidVariantIndex(selectedItem?.variants || [])
                    next[itemId] = firstValidIndex >= 0 ? firstValidIndex : undefined
                }

                return next
            })
            setSelectedItems({
                [itemId]: true,
            })
            setExpandedItems({
                [itemId]: true,
            })
            return
        }

        handleCardDeselect(itemId)
    }

    const handleLearnMore = (item) => {
        setModalWarrantyItem(item)
        onModalOpen()
    }

    const handleVariantChange = (itemId, variantIndex) => {
        setSelectedVariants((prev) => ({
            ...prev,
            [itemId]: variantIndex,
        }))
    }

    const getWarrantyPriceLabel = (variant) => {
        if (!variant) {
            return ''
        }

        const totalPrice = Number(variant?.price || 0)
        if (totalPrice <= 0) {
            return ''
        }

        return intl.formatNumber(totalPrice, {
            style: 'currency',
            currency: activeCurrency,
        })
    }

    return (
        <Stack {...styles.container} data-testid="product-care">
            <Stack {...styles.header}>
                <CareShieldIcon {...styles.headerIcon} />
                <Stack {...styles.headerTextBlock}>
                    <Text as="h3" id="product-care-title" {...styles.title}>
                        <FormattedMessage
                            id="product_view.care.title"
                            defaultMessage="De'Longhi Care"
                        />
                    </Text>
                    <Text id="product-care-subtitle" {...styles.subtitle}>
                        <FormattedMessage
                            id="product_view.care.subtitle"
                            defaultMessage="Small description here, lorem ipsum."
                        />
                    </Text>
                </Stack>
            </Stack>

            <Stack
                {...styles.list}
                role="radiogroup"
                aria-labelledby="product-care-title"
                aria-describedby="product-care-subtitle"
            >
                {careItems.map((item) => {
                    const isExpanded = expandedItems[item.id]
                    const isSelected = selectedItems[item.id]
                    const hasVariants = item.variants && item.variants.length > 0
                    const defaultVariantIndex = hasVariants
                        ? getFirstValidVariantIndex(item.variants)
                        : -1
                    const selectedVariantIndex = selectedVariants[item.id] ?? defaultVariantIndex
                    const safeSelectedVariantIndex =
                        selectedVariantIndex >= 0 ? selectedVariantIndex : 0
                    const displayTitle = item?.warrantyGroupName || item?.name
                    const displayDescription = getDisplayDescription(item)
                    const warrantyBadgeLabel = getWarrantyBadgeLabel(item)
                    const titleId = `product-care-item-title-${item.id}`
                    const descriptionId = `product-care-item-description-${item.id}`
                    const detailsId = `warranty-details-${item.id}`
                    const warrantyPlanOptions = hasVariants
                        ? item.variants.reduce((options, variant, index) => {
                              if (!isValidWarrantyProductId(variant?.productId)) {
                                  return options
                              }

                              const priceLabel = getWarrantyPriceLabel(variant)
                              const extensionLabel = getWarrantyOptionLabel(variant)

                              options.push({
                                  value: String(index),
                                  title: extensionLabel,
                                  priceLabel,
                                  label: `${extensionLabel}${
                                      priceLabel ? ` ${priceLabel}` : ''
                                  }`.trim(),
                              })

                              return options
                          }, [])
                        : []

                    return (
                        <Box
                            key={item.id}
                            {...styles.card}
                            bg={isSelected ? 'background.base.elevated' : styles?.card?.bg}
                            cursor="pointer"
                            onClick={() => handleCheckboxToggle(item.id, !isSelected)}
                        >
                            {/* Main Card Content */}
                            <Flex {...styles.cardRow} transition="all 0.2s">
                                <Stack {...styles.cardContent} flex={1}>
                                    <Text id={titleId} {...styles.cardTitle}>
                                        {displayTitle}
                                    </Text>
                                    <Flex gap={1} alignItems="baseline" wrap="wrap">
                                        <Text id={descriptionId} {...styles.cardDescription}>
                                            {displayDescription}
                                        </Text>
                                        <Text
                                            as="button"
                                            type="button"
                                            variant="link-button"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                handleLearnMore(item)
                                            }}
                                            {...styles.learnMore}
                                            aria-label={`Learn more about ${
                                                displayTitle || item?.id
                                            }`}
                                            _focusVisible={{
                                                outline: '2px solid',
                                                outlineColor: 'button.border.primary-focused',
                                                outlineOffset: '2px',
                                            }}
                                        >
                                            <FormattedMessage
                                                id="product_view.care.learn_more"
                                                defaultMessage="Learn more"
                                            />
                                        </Text>
                                        {warrantyBadgeLabel && (
                                            <Text {...styles.learnMoreBadge}>
                                                {warrantyBadgeLabel}
                                            </Text>
                                        )}
                                    </Flex>
                                </Stack>

                                <Box
                                    {...styles.checkboxWrapper}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Checkbox
                                        variant="pdp-recommendation-checkbox"
                                        isChecked={Boolean(isSelected)}
                                        role="radio"
                                        name="product-care-selection"
                                        aria-checked={Boolean(isSelected)}
                                        aria-labelledby={titleId}
                                        aria-describedby={descriptionId}
                                        aria-controls={isSelected ? detailsId : undefined}
                                        onChange={(e) => {
                                            handleCheckboxToggle(item.id, e.target.checked)
                                        }}
                                        aria-label={item?.name || item?.id}
                                    />
                                </Box>
                            </Flex>

                            {/* Expanded Content - Variant Selector & Learn More */}
                            {isExpanded && isSelected && (
                                <Stack
                                    id={detailsId}
                                    role="region"
                                    aria-labelledby={titleId}
                                    {...styles.warrantyDropdown}
                                >
                                    {/* Variant Dropdown */}
                                    {hasVariants && warrantyPlanOptions.length > 0 && (
                                        <Stack spacing={2}>
                                            <Box onClick={(e) => e.stopPropagation()}>
                                                <Dropdown
                                                    name={`warranty-plan-select-${item.id}`}
                                                    ariaLabel={`Warranty plan for ${
                                                        item?.name || item?.id
                                                    }`}
                                                    value={String(safeSelectedVariantIndex)}
                                                    // label={selectedCareLabel || undefined}
                                                    options={warrantyPlanOptions}
                                                    formatOptionLabel={(option) => (
                                                        <Flex {...styles.planOptionRow}>
                                                            <Text {...styles.planOptionTitle}>
                                                                {option.title || option.label}
                                                            </Text>
                                                            {option.priceLabel ? (
                                                                <Text {...styles.planOptionPrice}>
                                                                    {option.priceLabel}
                                                                </Text>
                                                            ) : null}
                                                        </Flex>
                                                    )}
                                                    onChange={(option) =>
                                                        handleVariantChange(
                                                            item.id,
                                                            parseInt(option?.value || '0', 10),
                                                        )
                                                    }
                                                />
                                            </Box>
                                        </Stack>
                                    )}
                                </Stack>
                            )}
                        </Box>
                    )
                })}
            </Stack>

            {/* Warranty Care Modal */}
            <WarrantyCareModal
                isOpen={isModalOpen}
                onClose={onModalClose}
                exploreLink={
                    warrantyTermsAndConditionsLink ||
                    modalWarrantyItem?.learnMoreHref ||
                    modalWarrantyItem?.rawRec?.warrantyTermsAndConditionsLink
                }
            />
        </Stack>
    )
}

ProductCare.propTypes = {
    recommendations: PropTypes.array,
    onSelectionChange: PropTypes.func,
}

export default ProductCare
